import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { FiArrowDown } from 'react-icons/fi';
import PullToRefresh from 'react-simple-pull-to-refresh';
import { useEntity } from '../../../../lib/entity';
import { MessagesProvider } from '../../../../lib/messages';
import { PER_PAGE } from '../../../../lib/supabase';
import getIdFromSlug from '../../../../lib/utils/getIdFromSlug';
import Button from '../../../Button';
import { DiscussionSkeleton } from '../../Discussion/DiscussionCard';
import { RoomSkeleton } from '../../RoomCard';
import { types } from '../Entity';

const DiscussionCard = dynamic(() => import('../../Discussion/DiscussionCard'), {
  loading: DiscussionSkeleton,
});

const RoomCard = dynamic(() => import('../../RoomCard'), { loading: RoomSkeleton });

const EntityMessages = dynamic(() => import('../../Entity/EntityMessages'), {
  ssr: false,
});

export default function EntityContent({ section, onCreate, searchQuery, items, isLoading, type }) {
  const router = useRouter();
  const { entitySlug } = router.query;

  const entityId = getIdFromSlug(entitySlug);

  const entity = useEntity().entity;
  const rooms = useEntity().rooms;
  const fetchRooms = useEntity().fetchRooms;
  const entityDispatch = useEntity().dispatch;
  const stateFetchMore = useEntity().state.canFetchMore;
  const stateIsLoading = useEntity().state.isLoading;

  const [canFetchMore, setCanFetchMore] = useState(false);

  const [load, setLoad] = useState(false);

  const [range, setRange] = useState({
    rangeFrom: 0,
    rangeTo: PER_PAGE,
  });

  const contentClasses = classNames('feed entity-wrapper rounded-lg');
  const sectionClasses = classNames({
    hidden: type !== section,
    section: section !== types.MESSAGES,
  });
  const messagesClasses = classNames({
    hidden: section !== types.MESSAGES,
  });

  const handleRefresh = () => {
    if (rooms.length) {
      return fetchRooms(entityDispatch, { entityId });
    }
    return Promise.resolve();
  };

  const handleFetchMore = () => {
    if (rooms.length) {
      fetchRooms(entityDispatch, {
        entityId,
        range: { rangeFrom: range.rangeFrom + PER_PAGE + 1, rangeTo: range.rangeTo + PER_PAGE + 1 },
      }).then(() =>
        setRange({
          rangeFrom: range.rangeFrom + PER_PAGE + 1,
          rangeTo: range.rangeTo + PER_PAGE + 1,
        }),
      );
    }
    return Promise.resolve();
  };

  useEffect(() => {
    if (section === types.MESSAGES) {
      setLoad(true);
    }
  }, [section]);

  useEffect(() => {
    setCanFetchMore(stateFetchMore && !stateIsLoading);
  }, [stateIsLoading, stateFetchMore]);

  return (
    <section className={contentClasses}>
      <section className={messagesClasses}>
        <MessagesProvider load={load}>
          <EntityMessages
            disableFetch={section !== types.MESSAGES}
            onCreate={onCreate}
            searchQuery={searchQuery}
            show={section === types.MESSAGES}
            isLoading={stateIsLoading || isLoading}
          />
        </MessagesProvider>
      </section>
      {[types.ROOMS, types.DISCUSSIONS].includes(section) && (
        <PullToRefresh
          pullDownThreshold={100}
          pullingContent={
            <section className="text-center space-y-4 py-2">
              <FiArrowDown className="w-6 h-6 mx-auto" />
              Pull down to refresh
            </section>
          }
          onRefresh={handleRefresh}
          canFetchMore={canFetchMore}
          onFetchMore={handleFetchMore}
          maxPullDownDistance={300}
        >
          <section className={sectionClasses}>
            <section className="wrapper">
              {section === types.ROOMS && (
                <>
                  {!isLoading && items.map((item) => <RoomCard key={item.id} room={item} />)}
                  {isLoading && [...Array(5).keys()].map((i) => <RoomSkeleton key={i} />)}
                </>
              )}
              {section === types.DISCUSSIONS && (
                <>
                  {!isLoading &&
                    items.map((item) => <DiscussionCard key={item.id} discussion={item} />)}
                  {isLoading && [...Array(5).keys()].map((i) => <DiscussionSkeleton key={i} />)}
                </>
              )}

              {section !== types.MESSAGES && !items.length && (
                <section className="mx-auto text-center space-y-4">
                  <h3 className="text-xl text-gray-400">
                    {section === types.DISCUSSIONS ? '🎙' : '🚪'}
                    <br />
                    No {section}{' '}
                    {searchQuery ? (
                      <>found for query "{searchQuery}"</>
                    ) : (
                      <>
                        for <strong>{entity?.title}</strong>.
                      </>
                    )}
                  </h3>
                  <Button onClick={onCreate}>Start one</Button>
                </section>
              )}
            </section>
          </section>
        </PullToRefresh>
      )}
    </section>
  );
}
